import { RenderSystem, RENDER_LAYERS, GridConfig } from './RenderSystem';
import { MonsterType } from '../entities/Monster';
import { CustomLevelManager, CustomLevel } from './CustomLevelManager';

// ============================================
// Level Format + Loader
// Updated with Regions, Curved Paths, Multi-Route
// ============================================

// Tree placement constants
const TREE_GENERATION = {
  SAFE_DISTANCE: 55,       // Minimum distance from path (monsters can't hit players)
  ARROW_RANGE: 130,        // Arrow tower range
  STRATEGIC_PERCENT: 0.6,  // 60% of trees within arrow range
  TREE_MULTIPLIER: 6,      // 6x the original tree count (2x previous 3x)
  MIN_TREE_SPACING: 70,    // Minimum distance between trees
  MAP_EDGE_PADDING: 60,    // Keep trees away from map edges
  BASE_EXCLUSION: 120,     // Keep trees away from base
  SPAWN_EXCLUSION: 80,     // Keep trees away from player spawns
};

// Position in tile coordinates
export interface TilePosition {
  x: number;
  y: number;
}

// Tree slot definition
export interface TreeSlotDef {
  x: number;
  y: number;
  id?: string;
}

// Path definition with optional routes
export interface PathDef {
  id: string;
  points?: TilePosition[];  // For simple paths
  routes?: Record<string, TilePosition[]>;  // For multi-route
  distribution?: Record<string, number>;  // Route probability
  curved?: boolean;
  curveResolution?: number;
}

// Spawn configuration in wave
export interface SpawnDef {
  monsterType: MonsterType;
  count: number;
  interval: number;     // Seconds between spawns
  delay?: number;       // Delay before first spawn
  pathId: string;       // Which path to use
  routeId?: string;     // Specific route (if multi-route)
}

// Boss spawn definition
export interface BossDef {
  type: string;         // Boss type (bronze_idol, earth_tower, etc.)
  pathId: string;
  routeId?: string;
  delay?: number;       // Delay before boss spawns
  hpOverride?: number;  // Override boss HP for this level
  speedMultiplier?: number;  // Multiply phase speeds (e.g. 0.5 = 2x slower)
}

// Wave definition
export interface WaveDef {
  waveNumber: number;
  spawns: SpawnDef[];
  boss?: BossDef;       // Optional boss for this wave
}

// Region types for monster distribution
export type RegionType = 'forest' | 'swamp' | 'ruins' | 'volcano';

// Biome types for visuals (grass + trees): forest, bog/swamp, ruins/highlands, volcano
export type BiomeType = 'forest' | 'bog' | 'ruins' | 'volcano';

// Tower types for allowed towers
import { TowerType } from '../entities/Tower';

// Complete scenario definition
export interface ScenarioDef {
  id: string;
  name: string;
  description: string;
  difficulty: number;     // 1-5
  region: RegionType;
  regionNumber: number;   // 1-4 for region grouping
  isChallenge?: boolean;  // Optional challenge scenario
  isGate?: boolean;       // Gate to next region
  mapSize: { w: number; h: number };
  tileSize?: number;      // Default 90 for 1080p
  base: TilePosition;
  playerSpawns: TilePosition[];
  treeSlots: TreeSlotDef[];
  paths: PathDef[];
  waves: WaveDef[];
  startingCoins: number;  // For buying towers
  startingGems?: number;  // For upgrades/research
  startingVillagers?: number;
  allowedTowers: TowerType[];  // Towers available in this scenario
  unlocks: string[];      // Scenario IDs unlocked on completion
}

// Loaded level data (with world coordinates calculated)
export interface LoadedLevel {
  scenario: ScenarioDef;
  gridConfig: GridConfig;
  baseWorldPos: { x: number; y: number };
  playerSpawnWorldPos: Array<{ x: number; y: number }>;
  treeSlotWorldPos: Array<{ x: number; y: number; id: string }>;
  pathsWorld: Array<{
    id: string;
    points?: Array<{ x: number; y: number }>;
    routes?: Record<string, Array<{ x: number; y: number }>>;
    distribution?: Record<string, number>;
    curved: boolean;
    curveResolution: number;
    totalLength: number;
    segmentLengths: number[];
  }>;
}

// ============================================
// Region-Based Monster Distribution
// ============================================

const REGION_MONSTERS: Record<RegionType, MonsterType[]> = {
  forest: ['crawler', 'runner', 'bat'],
  swamp: ['brute', 'shielded', 'flyer', 'crawler'],
  ruins: ['shielded', 'flyer', 'swarm', 'bat'],
  volcano: ['juggernaut', 'phased', 'swarm', 'brute'],
};

// ============================================
// Scenario Data - All levels defined here
// ============================================

const SCENARIOS: Record<string, ScenarioDef> = {
  // ============================================
  // Tutorial — Same map as F1, guided steps, one wave with one crawler
  // ============================================
  'Tutorial': {
    id: 'Tutorial',
    name: 'Tutorial',
    description: 'Learn how to defend your village.',
    difficulty: 1,
    region: 'forest',
    regionNumber: 1,
    mapSize: { w: 20, h: 12 },
    base: { x: 18, y: 6 },
    playerSpawns: [{ x: 10, y: 6 }, { x: 11, y: 6 }],
    treeSlots: [
      { x: 4, y: 4 }, { x: 4, y: 8 },
      { x: 8, y: 3 }, { x: 8, y: 9 },
      { x: 12, y: 4 }, { x: 12, y: 8 },
      { x: 15, y: 5 }, { x: 15, y: 7 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 6 }, { x: 3, y: 6 }, { x: 6, y: 4 },
      { x: 10, y: 6 }, { x: 14, y: 8 }, { x: 18, y: 6 },
    ], curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 1, interval: 1, pathId: 'main' }] },
    ],
    startingCoins: 300,
    startingGems: 5,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon'],
    unlocks: ['F1'],
  },

  // ============================================
  // REGION 1 — Forest Edge (Starter)
  // F1 → F2 → F3 → F4 (Gate)
  //        ↘ F2C (Challenge)
  // ============================================
  
  'F1': {
    id: 'F1',
    name: 'First Grove',
    description: 'A peaceful clearing. Your first defense.',
    difficulty: 1,
    region: 'forest',
    regionNumber: 1,
    mapSize: { w: 20, h: 12 },
    base: { x: 18, y: 6 },
    playerSpawns: [{ x: 10, y: 6 }, { x: 11, y: 6 }],
    treeSlots: [
      { x: 4, y: 4 }, { x: 4, y: 8 },
      { x: 8, y: 3 }, { x: 8, y: 9 },
      { x: 12, y: 4 }, { x: 12, y: 8 },
      { x: 15, y: 5 }, { x: 15, y: 7 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 6 }, { x: 3, y: 6 }, { x: 6, y: 4 },
      { x: 10, y: 6 }, { x: 14, y: 8 }, { x: 18, y: 6 },
    ], curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 8, interval: 1.5, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'crawler', count: 10, interval: 1.3, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'crawler', count: 6, interval: 1.2, pathId: 'main' },
        { monsterType: 'runner', count: 4, interval: 0.8, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'crawler', count: 12, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 5, spawns: [{ monsterType: 'runner', count: 8, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 6, spawns: [{ monsterType: 'brute', count: 1, interval: 1, pathId: 'main' }] },
    ],
    startingCoins: 300,
    startingGems: 5,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon'],
    unlocks: ['F2'],
  },

  'F2': {
    id: 'F2',
    name: 'Split Path',
    description: 'The path divides. Air threats emerge.',
    difficulty: 1,
    region: 'forest',
    regionNumber: 1,
    mapSize: { w: 20, h: 12 },
    base: { x: 18, y: 6 },
    playerSpawns: [{ x: 10, y: 6 }, { x: 11, y: 6 }],
    treeSlots: [
      { x: 3, y: 3 }, { x: 3, y: 9 },
      { x: 7, y: 5 }, { x: 7, y: 7 },
      { x: 11, y: 3 }, { x: 11, y: 9 },
      { x: 15, y: 5 }, { x: 15, y: 7 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 6 }, { x: 4, y: 4 }, { x: 8, y: 6 },
      { x: 12, y: 8 }, { x: 16, y: 6 }, { x: 18, y: 6 },
    ], curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 10, interval: 1.3, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'crawler', count: 12, interval: 1.2, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'runner', count: 6, interval: 0.9, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'crawler', count: 8, interval: 1.0, pathId: 'main' },
        { monsterType: 'runner', count: 4, interval: 0.8, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [{ monsterType: 'bat', count: 6, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 6, spawns: [
        { monsterType: 'crawler', count: 5, interval: 1.0, pathId: 'main' },
        { monsterType: 'runner', count: 5, interval: 0.8, delay: 3, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'brute', count: 1, interval: 1, pathId: 'main' }] },
    ],
    startingCoins: 325,
    startingGems: 8,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair'],
    unlocks: ['F3', 'F2C'],
  },

  'F2C': {
    id: 'F2C',
    name: 'Forest Challenge',
    description: 'Arrow towers only. Dense runner waves.',
    difficulty: 2,
    region: 'forest',
    regionNumber: 1,
    isChallenge: true,
    mapSize: { w: 20, h: 12 },
    base: { x: 18, y: 6 },
    playerSpawns: [{ x: 10, y: 6 }, { x: 11, y: 6 }],
    treeSlots: [
      { x: 4, y: 4 }, { x: 4, y: 8 },
      { x: 8, y: 5 }, { x: 8, y: 7 },
      { x: 12, y: 4 }, { x: 12, y: 8 },
      { x: 15, y: 6 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 6 }, { x: 5, y: 6 }, { x: 10, y: 6 }, { x: 15, y: 6 }, { x: 18, y: 6 },
    ], curved: false }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'runner', count: 8, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'runner', count: 10, interval: 0.7, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'runner', count: 12, interval: 0.6, pathId: 'main' }] },
      { waveNumber: 4, spawns: [{ monsterType: 'runner', count: 15, interval: 0.5, pathId: 'main' }] },
      { waveNumber: 5, spawns: [{ monsterType: 'crawler', count: 10, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 6, spawns: [{ monsterType: 'runner', count: 18, interval: 0.4, pathId: 'main' }] },
      { waveNumber: 7, spawns: [
        { monsterType: 'runner', count: 10, interval: 0.5, pathId: 'main' },
        { monsterType: 'crawler', count: 8, interval: 0.6, delay: 3, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [{ monsterType: 'runner', count: 25, interval: 0.3, pathId: 'main' }] },
    ],
    startingCoins: 250,
    startingGems: 3,
    startingVillagers: 15,
    allowedTowers: ['arrow'],
    unlocks: [],
  },

  'F3': {
    id: 'F3',
    name: 'Double Curve',
    description: 'A winding path with shielded enemies.',
    difficulty: 2,
    region: 'forest',
    regionNumber: 1,
    mapSize: { w: 22, h: 12 },
    base: { x: 20, y: 6 },
    playerSpawns: [{ x: 11, y: 6 }, { x: 12, y: 6 }],
    treeSlots: [
      { x: 3, y: 4 }, { x: 3, y: 8 },
      { x: 7, y: 3 }, { x: 7, y: 9 },
      { x: 11, y: 5 }, { x: 11, y: 7 },
      { x: 15, y: 3 }, { x: 15, y: 9 },
      { x: 18, y: 5 }, { x: 18, y: 7 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 6 }, { x: 4, y: 3 }, { x: 8, y: 6 },
      { x: 12, y: 9 }, { x: 16, y: 6 }, { x: 20, y: 6 },
    ], curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 10, interval: 1.2, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'runner', count: 8, interval: 0.9, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'shielded', count: 4, interval: 2.0, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'crawler', count: 8, interval: 1.0, pathId: 'main' },
        { monsterType: 'bat', count: 4, interval: 1.2, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [
        { monsterType: 'shielded', count: 5, interval: 1.8, pathId: 'main' },
        { monsterType: 'runner', count: 6, interval: 0.8, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [{ monsterType: 'bat', count: 8, interval: 0.9, pathId: 'main' }] },
      { waveNumber: 7, spawns: [
        { monsterType: 'crawler', count: 10, interval: 0.8, pathId: 'main' },
        { monsterType: 'shielded', count: 4, interval: 2.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [{ monsterType: 'brute', count: 2, interval: 3.0, pathId: 'main' }] },
    ],
    startingCoins: 350,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla'],
    unlocks: ['F4'],
  },

  'F4': {
    id: 'F4',
    name: 'Forest Gate',
    description: 'The gateway to the swamp. Defeat the Brute Boss.',
    difficulty: 2,
    region: 'forest',
    regionNumber: 1,
    isGate: true,
    mapSize: { w: 22, h: 12 },
    base: { x: 20, y: 6 },
    playerSpawns: [{ x: 11, y: 6 }, { x: 12, y: 6 }],
    treeSlots: [
      { x: 3, y: 4 }, { x: 3, y: 8 },
      { x: 7, y: 3 }, { x: 7, y: 9 },
      { x: 11, y: 4 }, { x: 11, y: 8 },
      { x: 15, y: 3 }, { x: 15, y: 9 },
      { x: 18, y: 5 }, { x: 18, y: 7 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 6 }, { x: 4, y: 4 }, { x: 8, y: 7 },
      { x: 12, y: 5 }, { x: 16, y: 7 }, { x: 20, y: 6 },
    ], curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 12, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'runner', count: 10, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'shielded', count: 5, interval: 1.8, pathId: 'main' },
        { monsterType: 'crawler', count: 6, interval: 1.0, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'bat', count: 10, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'runner', count: 8, interval: 0.7, pathId: 'main' },
        { monsterType: 'shielded', count: 4, interval: 1.5, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [
        { monsterType: 'crawler', count: 10, interval: 0.8, pathId: 'main' },
        { monsterType: 'bat', count: 6, interval: 1.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'shielded', count: 8, interval: 1.2, pathId: 'main' }] },
      { waveNumber: 8, spawns: [
        { monsterType: 'runner', count: 12, interval: 0.5, pathId: 'main' },
        { monsterType: 'bat', count: 8, interval: 0.8, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'crawler', count: 15, interval: 0.6, pathId: 'main' },
        { monsterType: 'shielded', count: 5, interval: 1.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [], boss: { type: 'brute_boss', pathId: 'main', delay: 0, hpOverride: 1300, speedMultiplier: 0.5 }},
    ],
    startingCoins: 375,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla'],
    unlocks: ['S1'],
  },

  // ============================================
  // REGION 2 — Swamp & River
  // S1 → S2 → S3 → S4 (Gate)
  //        ↘ S2C (Challenge)
  // ============================================

  'S1': {
    id: 'S1',
    name: 'Mud Flats',
    description: 'Swampy terrain. Flyers begin appearing.',
    difficulty: 2,
    region: 'swamp',
    regionNumber: 2,
    mapSize: { w: 22, h: 14 },
    base: { x: 20, y: 7 },
    playerSpawns: [{ x: 11, y: 7 }, { x: 12, y: 7 }],
    treeSlots: [
      { x: 3, y: 5 }, { x: 3, y: 9 },
      { x: 7, y: 4 }, { x: 7, y: 10 },
      { x: 11, y: 5 }, { x: 11, y: 9 },
      { x: 15, y: 4 }, { x: 15, y: 10 },
      { x: 18, y: 6 }, { x: 18, y: 8 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 7 }, { x: 4, y: 5 }, { x: 8, y: 8 },
      { x: 12, y: 6 }, { x: 16, y: 8 }, { x: 20, y: 7 },
    ], curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 10, interval: 1.2, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'shielded', count: 5, interval: 1.8, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'flyer', count: 6, interval: 1.2, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'crawler', count: 8, interval: 1.0, pathId: 'main' },
        { monsterType: 'flyer', count: 4, interval: 1.5, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [{ monsterType: 'brute', count: 3, interval: 2.5, pathId: 'main' }] },
      { waveNumber: 6, spawns: [
        { monsterType: 'shielded', count: 6, interval: 1.5, pathId: 'main' },
        { monsterType: 'flyer', count: 5, interval: 1.2, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'runner', count: 15, interval: 0.6, pathId: 'main' }] },
      { waveNumber: 8, spawns: [
        { monsterType: 'brute', count: 2, interval: 3.0, pathId: 'main' },
        { monsterType: 'flyer', count: 6, interval: 1.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'shielded', count: 8, interval: 1.2, pathId: 'main' },
        { monsterType: 'crawler', count: 10, interval: 0.8, delay: 5, pathId: 'main' },
      ]},
    ],
    startingCoins: 425,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser'],
    unlocks: ['S2'],
  },

  'S2': {
    id: 'S2',
    name: 'Forked River',
    description: 'Two paths converge. Mortar unlocked.',
    difficulty: 3,
    region: 'swamp',
    regionNumber: 2,
    mapSize: { w: 22, h: 14 },
    base: { x: 11, y: 7 },
    playerSpawns: [{ x: 10, y: 7 }, { x: 12, y: 7 }],
    treeSlots: [
      { x: 3, y: 5 }, { x: 3, y: 9 },
      { x: 7, y: 4 }, { x: 7, y: 10 },
      { x: 15, y: 4 }, { x: 15, y: 10 },
      { x: 19, y: 5 }, { x: 19, y: 9 },
    ],
    paths: [{ id: 'main', routes: {
      'left': [{ x: -1, y: 7 }, { x: 4, y: 5 }, { x: 8, y: 7 }, { x: 11, y: 7 }],
      'right': [{ x: 23, y: 7 }, { x: 18, y: 9 }, { x: 14, y: 7 }, { x: 11, y: 7 }],
    }, distribution: { 'left': 0.5, 'right': 0.5 }, curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 12, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'shielded', count: 6, interval: 1.5, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'flyer', count: 8, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'brute', count: 3, interval: 2.5, pathId: 'main' },
        { monsterType: 'crawler', count: 8, interval: 1.0, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [{ monsterType: 'swarm', count: 15, interval: 0.4, pathId: 'main' }] },
      { waveNumber: 6, spawns: [
        { monsterType: 'shielded', count: 8, interval: 1.2, pathId: 'main' },
        { monsterType: 'flyer', count: 6, interval: 1.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'brute', count: 4, interval: 2.0, pathId: 'main' }] },
      { waveNumber: 8, spawns: [
        { monsterType: 'swarm', count: 20, interval: 0.3, pathId: 'main' },
        { monsterType: 'flyer', count: 8, interval: 0.8, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'shielded', count: 10, interval: 1.0, pathId: 'main' },
        { monsterType: 'brute', count: 3, interval: 2.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'crawler', count: 15, interval: 0.6, pathId: 'main' },
        { monsterType: 'flyer', count: 10, interval: 0.7, delay: 5, pathId: 'main' },
      ]},
    ],
    startingCoins: 450,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar'],
    unlocks: ['S3', 'S2C'],
  },

  'S2C': {
    id: 'S2C',
    name: 'Swamp Challenge',
    description: 'Cannon and Mortar only. Heavy ground tanks.',
    difficulty: 3,
    region: 'swamp',
    regionNumber: 2,
    isChallenge: true,
    mapSize: { w: 22, h: 14 },
    base: { x: 20, y: 7 },
    playerSpawns: [{ x: 11, y: 7 }, { x: 12, y: 7 }],
    treeSlots: [
      { x: 4, y: 5 }, { x: 4, y: 9 },
      { x: 8, y: 6 }, { x: 8, y: 8 },
      { x: 12, y: 5 }, { x: 12, y: 9 },
      { x: 16, y: 6 }, { x: 16, y: 8 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 7 }, { x: 5, y: 7 }, { x: 10, y: 7 }, { x: 15, y: 7 }, { x: 20, y: 7 },
    ], curved: false }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'brute', count: 3, interval: 2.5, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'shielded', count: 8, interval: 1.2, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'brute', count: 4, interval: 2.0, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'shielded', count: 10, interval: 1.0, pathId: 'main' },
        { monsterType: 'brute', count: 2, interval: 3.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [{ monsterType: 'juggernaut', count: 1, interval: 1, pathId: 'main' }] },
      { waveNumber: 6, spawns: [
        { monsterType: 'brute', count: 5, interval: 1.8, pathId: 'main' },
        { monsterType: 'shielded', count: 8, interval: 1.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'juggernaut', count: 2, interval: 4.0, pathId: 'main' }] },
      { waveNumber: 8, spawns: [
        { monsterType: 'shielded', count: 12, interval: 0.8, pathId: 'main' },
        { monsterType: 'brute', count: 4, interval: 2.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'juggernaut', count: 2, interval: 3.5, pathId: 'main' },
        { monsterType: 'brute', count: 3, interval: 2.5, delay: 5, pathId: 'main' },
      ]},
    ],
    startingCoins: 300,
    startingGems: 5,
    startingVillagers: 15,
    allowedTowers: ['cannon', 'mortar'],
    unlocks: [],
  },

  'S3': {
    id: 'S3',
    name: 'Bog Maze',
    description: 'Winding swamp paths. Ice tower unlocked.',
    difficulty: 3,
    region: 'swamp',
    regionNumber: 2,
    mapSize: { w: 24, h: 14 },
    base: { x: 22, y: 7 },
    playerSpawns: [{ x: 12, y: 7 }, { x: 13, y: 7 }],
    treeSlots: [
      { x: 3, y: 4 }, { x: 3, y: 10 },
      { x: 7, y: 6 }, { x: 7, y: 8 },
      { x: 11, y: 4 }, { x: 11, y: 10 },
      { x: 15, y: 6 }, { x: 15, y: 8 },
      { x: 19, y: 5 }, { x: 19, y: 9 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 7 }, { x: 4, y: 4 }, { x: 8, y: 7 },
      { x: 12, y: 10 }, { x: 16, y: 7 }, { x: 20, y: 6 }, { x: 22, y: 7 },
    ], curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 12, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 8, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'shielded', count: 8, interval: 1.2, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'brute', count: 4, interval: 2.0, pathId: 'main' },
        { monsterType: 'swarm', count: 12, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [{ monsterType: 'flyer', count: 12, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 6, spawns: [
        { monsterType: 'shielded', count: 10, interval: 1.0, pathId: 'main' },
        { monsterType: 'flyer', count: 8, interval: 0.9, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'swarm', count: 25, interval: 0.3, pathId: 'main' }] },
      { waveNumber: 8, spawns: [
        { monsterType: 'brute', count: 5, interval: 1.8, pathId: 'main' },
        { monsterType: 'crawler', count: 12, interval: 0.7, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'flyer', count: 10, interval: 0.7, pathId: 'main' },
        { monsterType: 'shielded', count: 8, interval: 1.0, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'swarm', count: 20, interval: 0.3, pathId: 'main' },
        { monsterType: 'brute', count: 4, interval: 2.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [{ monsterType: 'juggernaut', count: 2, interval: 4.0, pathId: 'main' }] },
    ],
    startingCoins: 475,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice'],
    unlocks: ['S4'],
  },

  'S4': {
    id: 'S4',
    name: 'Swamp Gate',
    description: 'Gateway to the ruins. Defeat the Earth Tower.',
    difficulty: 3,
    region: 'swamp',
    regionNumber: 2,
    isGate: true,
    mapSize: { w: 24, h: 14 },
    base: { x: 12, y: 7 },
    playerSpawns: [{ x: 11, y: 7 }, { x: 13, y: 7 }],
    treeSlots: [
      { x: 3, y: 5 }, { x: 3, y: 9 },
      { x: 7, y: 4 }, { x: 7, y: 10 },
      { x: 17, y: 4 }, { x: 17, y: 10 },
      { x: 21, y: 5 }, { x: 21, y: 9 },
    ],
    paths: [{ id: 'main', routes: {
      'left': [{ x: -1, y: 7 }, { x: 4, y: 5 }, { x: 8, y: 7 }, { x: 12, y: 7 }],
      'right': [{ x: 25, y: 7 }, { x: 20, y: 9 }, { x: 16, y: 7 }, { x: 12, y: 7 }],
    }, distribution: { 'left': 0.5, 'right': 0.5 }, curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 15, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'shielded', count: 10, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'flyer', count: 12, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'brute', count: 5, interval: 1.8, pathId: 'main' },
        { monsterType: 'swarm', count: 15, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [
        { monsterType: 'shielded', count: 12, interval: 0.8, pathId: 'main' },
        { monsterType: 'flyer', count: 8, interval: 0.9, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [{ monsterType: 'juggernaut', count: 2, interval: 4.0, pathId: 'main' }] },
      { waveNumber: 7, spawns: [
        { monsterType: 'swarm', count: 25, interval: 0.25, pathId: 'main' },
        { monsterType: 'brute', count: 4, interval: 2.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 15, interval: 0.6, pathId: 'main' },
        { monsterType: 'shielded', count: 10, interval: 0.9, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'brute', count: 6, interval: 1.5, pathId: 'main' },
        { monsterType: 'juggernaut', count: 2, interval: 4.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'crawler', count: 20, interval: 0.5, pathId: 'main' },
        { monsterType: 'swarm', count: 20, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'shielded', count: 15, interval: 0.7, pathId: 'main' },
        { monsterType: 'flyer', count: 12, interval: 0.6, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 12, spawns: [], boss: { type: 'earth_tower', pathId: 'main', delay: 0, hpOverride: 1100, speedMultiplier: 0.85 }},
    ],
    startingCoins: 500,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice'],
    unlocks: ['R1'],
  },

  // ============================================
  // REGION 3 — Ruins & Highlands
  // R1 → R2 → R3 → R4 (Gate)
  //        ↘ R2C (Challenge)
  // ============================================

  'R1': {
    id: 'R1',
    name: 'Broken Roads',
    description: 'Ancient roads with mixed resist units. Fire tower unlocked.',
    difficulty: 3,
    region: 'ruins',
    regionNumber: 3,
    mapSize: { w: 24, h: 14 },
    base: { x: 22, y: 7 },
    playerSpawns: [{ x: 12, y: 7 }, { x: 13, y: 7 }],
    treeSlots: [
      { x: 3, y: 4 }, { x: 3, y: 10 },
      { x: 8, y: 5 }, { x: 8, y: 9 },
      { x: 13, y: 4 }, { x: 13, y: 10 },
      { x: 18, y: 5 }, { x: 18, y: 9 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 7 }, { x: 4, y: 5 }, { x: 9, y: 8 },
      { x: 14, y: 5 }, { x: 19, y: 8 }, { x: 22, y: 7 },
    ], curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 10, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 10, interval: 0.9, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'brute', count: 4, interval: 2.0, pathId: 'main' },
        { monsterType: 'bat', count: 8, interval: 0.8, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'swarm', count: 20, interval: 0.3, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'shielded', count: 12, interval: 0.8, pathId: 'main' },
        { monsterType: 'flyer', count: 10, interval: 0.7, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [{ monsterType: 'juggernaut', count: 2, interval: 4.0, pathId: 'main' }] },
      { waveNumber: 7, spawns: [
        { monsterType: 'swarm', count: 25, interval: 0.25, pathId: 'main' },
        { monsterType: 'brute', count: 5, interval: 1.8, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 15, interval: 0.6, pathId: 'main' },
        { monsterType: 'bat', count: 10, interval: 0.7, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'shielded', count: 15, interval: 0.7, pathId: 'main' },
        { monsterType: 'juggernaut', count: 2, interval: 4.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'brute', count: 6, interval: 1.5, pathId: 'main' },
        { monsterType: 'swarm', count: 30, interval: 0.2, delay: 5, pathId: 'main' },
      ]},
    ],
    startingCoins: 550,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire'],
    unlocks: ['R2'],
  },

  'R2': {
    id: 'R2',
    name: 'Twin Roads',
    description: 'Parallel routes converge. Hive tower unlocked.',
    difficulty: 4,
    region: 'ruins',
    regionNumber: 3,
    mapSize: { w: 24, h: 16 },
    base: { x: 12, y: 8 },
    playerSpawns: [{ x: 11, y: 8 }, { x: 13, y: 8 }],
    treeSlots: [
      { x: 2, y: 4 }, { x: 2, y: 12 },
      { x: 6, y: 6 }, { x: 6, y: 10 },
      { x: 18, y: 6 }, { x: 18, y: 10 },
      { x: 22, y: 4 }, { x: 22, y: 12 },
    ],
    paths: [{ id: 'main', routes: {
      'top': [{ x: -1, y: 4 }, { x: 4, y: 4 }, { x: 8, y: 6 }, { x: 12, y: 8 }],
      'bottom': [{ x: -1, y: 12 }, { x: 4, y: 12 }, { x: 8, y: 10 }, { x: 12, y: 8 }],
    }, distribution: { 'top': 0.5, 'bottom': 0.5 }, curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 12, interval: 0.9, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 12, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'brute', count: 5, interval: 1.8, pathId: 'main' },
        { monsterType: 'swarm', count: 15, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'juggernaut', count: 3, interval: 3.5, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'shielded', count: 15, interval: 0.7, pathId: 'main' },
        { monsterType: 'flyer', count: 12, interval: 0.6, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [
        { monsterType: 'swarm', count: 30, interval: 0.2, pathId: 'main' },
        { monsterType: 'brute', count: 5, interval: 1.8, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'phased', count: 3, interval: 3.0, pathId: 'main' }] },
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 18, interval: 0.5, pathId: 'main' },
        { monsterType: 'bat', count: 12, interval: 0.6, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'juggernaut', count: 3, interval: 3.5, pathId: 'main' },
        { monsterType: 'shielded', count: 15, interval: 0.6, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'swarm', count: 35, interval: 0.15, pathId: 'main' },
        { monsterType: 'phased', count: 3, interval: 3.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'brute', count: 8, interval: 1.2, pathId: 'main' },
        { monsterType: 'flyer', count: 15, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
    ],
    startingCoins: 575,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire', 'hive'],
    unlocks: ['R3', 'R2C'],
  },

  'R2C': {
    id: 'R2C',
    name: 'Ruins Challenge',
    description: 'Tesla and Fire only. Mixed elemental waves.',
    difficulty: 4,
    region: 'ruins',
    regionNumber: 3,
    isChallenge: true,
    mapSize: { w: 22, h: 14 },
    base: { x: 20, y: 7 },
    playerSpawns: [{ x: 11, y: 7 }, { x: 12, y: 7 }],
    treeSlots: [
      { x: 4, y: 5 }, { x: 4, y: 9 },
      { x: 9, y: 6 }, { x: 9, y: 8 },
      { x: 14, y: 5 }, { x: 14, y: 9 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 7 }, { x: 5, y: 5 }, { x: 10, y: 8 }, { x: 15, y: 6 }, { x: 20, y: 7 },
    ], curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 15, interval: 0.8, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'shielded', count: 10, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 3, spawns: [{ monsterType: 'swarm', count: 20, interval: 0.3, pathId: 'main' }] },
      { waveNumber: 4, spawns: [
        { monsterType: 'brute', count: 5, interval: 1.8, pathId: 'main' },
        { monsterType: 'shielded', count: 10, interval: 0.9, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 5, spawns: [{ monsterType: 'juggernaut', count: 2, interval: 4.0, pathId: 'main' }] },
      { waveNumber: 6, spawns: [
        { monsterType: 'swarm', count: 30, interval: 0.2, pathId: 'main' },
        { monsterType: 'brute', count: 4, interval: 2.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [{ monsterType: 'phased', count: 4, interval: 2.5, pathId: 'main' }] },
      { waveNumber: 8, spawns: [
        { monsterType: 'shielded', count: 15, interval: 0.7, pathId: 'main' },
        { monsterType: 'juggernaut', count: 2, interval: 4.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'swarm', count: 35, interval: 0.15, pathId: 'main' },
        { monsterType: 'phased', count: 3, interval: 3.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'brute', count: 8, interval: 1.2, pathId: 'main' },
        { monsterType: 'juggernaut', count: 3, interval: 3.5, delay: 6, pathId: 'main' },
      ]},
    ],
    startingCoins: 350,
    startingGems: 8,
    startingVillagers: 15,
    allowedTowers: ['tesla', 'fire'],
    unlocks: [],
  },

  'R3': {
    id: 'R3',
    name: 'Spiral Ridge',
    description: 'Looping curved paths. Trap tower unlocked.',
    difficulty: 4,
    region: 'ruins',
    regionNumber: 3,
    mapSize: { w: 26, h: 16 },
    base: { x: 13, y: 8 },
    playerSpawns: [{ x: 12, y: 8 }, { x: 14, y: 8 }],
    treeSlots: [
      { x: 3, y: 4 }, { x: 3, y: 12 },
      { x: 8, y: 6 }, { x: 8, y: 10 },
      { x: 18, y: 6 }, { x: 18, y: 10 },
      { x: 23, y: 4 }, { x: 23, y: 12 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 8 }, { x: 4, y: 4 }, { x: 9, y: 8 },
      { x: 4, y: 12 }, { x: 13, y: 8 },
    ], curved: true, curveResolution: 12 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 15, interval: 0.7, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 15, interval: 0.6, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'brute', count: 6, interval: 1.5, pathId: 'main' },
        { monsterType: 'swarm', count: 20, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'juggernaut', count: 3, interval: 3.5, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'phased', count: 4, interval: 2.5, pathId: 'main' },
        { monsterType: 'shielded', count: 15, interval: 0.6, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [
        { monsterType: 'swarm', count: 35, interval: 0.15, pathId: 'main' },
        { monsterType: 'flyer', count: 15, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [
        { monsterType: 'brute', count: 8, interval: 1.2, pathId: 'main' },
        { monsterType: 'juggernaut', count: 3, interval: 3.5, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 20, interval: 0.4, pathId: 'main' },
        { monsterType: 'bat', count: 15, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'phased', count: 5, interval: 2.0, pathId: 'main' },
        { monsterType: 'shielded', count: 18, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'swarm', count: 40, interval: 0.12, pathId: 'main' },
        { monsterType: 'brute', count: 6, interval: 1.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'juggernaut', count: 4, interval: 3.0, pathId: 'main' },
        { monsterType: 'phased', count: 4, interval: 2.5, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 12, spawns: [
        { monsterType: 'flyer', count: 25, interval: 0.35, pathId: 'main' },
        { monsterType: 'shielded', count: 20, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
    ],
    startingCoins: 600,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire', 'hive', 'trap'],
    unlocks: ['R4'],
  },

  'R4': {
    id: 'R4',
    name: 'Ruins Gate',
    description: 'Gateway to the volcano. Defeat the Snail Beast.',
    difficulty: 4,
    region: 'ruins',
    regionNumber: 3,
    isGate: true,
    mapSize: { w: 26, h: 16 },
    base: { x: 13, y: 8 },
    playerSpawns: [{ x: 12, y: 8 }, { x: 14, y: 8 }],
    treeSlots: [
      { x: 2, y: 4 }, { x: 2, y: 12 },
      { x: 7, y: 6 }, { x: 7, y: 10 },
      { x: 19, y: 6 }, { x: 19, y: 10 },
      { x: 24, y: 4 }, { x: 24, y: 12 },
    ],
    paths: [{ id: 'main', routes: {
      'top': [{ x: -1, y: 4 }, { x: 5, y: 4 }, { x: 9, y: 6 }, { x: 13, y: 8 }],
      'bottom': [{ x: -1, y: 12 }, { x: 5, y: 12 }, { x: 9, y: 10 }, { x: 13, y: 8 }],
      'right': [{ x: 27, y: 8 }, { x: 21, y: 6 }, { x: 17, y: 8 }, { x: 13, y: 8 }],
    }, distribution: { 'top': 0.35, 'bottom': 0.35, 'right': 0.3 }, curved: true, curveResolution: 8 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 18, interval: 0.6, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 18, interval: 0.5, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'brute', count: 7, interval: 1.3, pathId: 'main' },
        { monsterType: 'swarm', count: 25, interval: 0.25, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'juggernaut', count: 4, interval: 3.0, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'phased', count: 5, interval: 2.0, pathId: 'main' },
        { monsterType: 'shielded', count: 18, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [
        { monsterType: 'swarm', count: 40, interval: 0.12, pathId: 'main' },
        { monsterType: 'flyer', count: 18, interval: 0.45, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [
        { monsterType: 'brute', count: 10, interval: 1.0, pathId: 'main' },
        { monsterType: 'juggernaut', count: 4, interval: 3.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 25, interval: 0.35, pathId: 'main' },
        { monsterType: 'bat', count: 18, interval: 0.45, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'phased', count: 6, interval: 1.8, pathId: 'main' },
        { monsterType: 'shielded', count: 20, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'swarm', count: 50, interval: 0.1, pathId: 'main' },
        { monsterType: 'brute', count: 8, interval: 1.2, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'juggernaut', count: 5, interval: 2.5, pathId: 'main' },
        { monsterType: 'phased', count: 5, interval: 2.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 12, spawns: [
        { monsterType: 'flyer', count: 30, interval: 0.3, pathId: 'main' },
        { monsterType: 'shielded', count: 25, interval: 0.35, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 13, spawns: [], boss: { type: 'snail_beast', pathId: 'main', delay: 0 }},
    ],
    startingCoins: 650,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire', 'hive', 'trap'],
    unlocks: ['V1'],
  },

  // ============================================
  // REGION 4 — Volcano Core (Endgame)
  // V1 → V2 → V3 → V4 (Final Boss)
  //        ↘ V2C (Challenge)
  // ============================================

  'V1': {
    id: 'V1',
    name: 'Lava Flow',
    description: 'The volcano awaits. All towers available.',
    difficulty: 4,
    region: 'volcano',
    regionNumber: 4,
    mapSize: { w: 26, h: 18 },
    base: { x: 24, y: 9 },
    playerSpawns: [{ x: 13, y: 9 }, { x: 14, y: 9 }],
    treeSlots: [
      { x: 3, y: 5 }, { x: 3, y: 13 },
      { x: 8, y: 7 }, { x: 8, y: 11 },
      { x: 14, y: 5 }, { x: 14, y: 13 },
      { x: 20, y: 7 }, { x: 20, y: 11 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 9 }, { x: 5, y: 5 }, { x: 10, y: 9 },
      { x: 15, y: 13 }, { x: 20, y: 9 }, { x: 24, y: 9 },
    ], curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 18, interval: 0.6, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 18, interval: 0.5, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'brute', count: 8, interval: 1.2, pathId: 'main' },
        { monsterType: 'swarm', count: 25, interval: 0.25, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'juggernaut', count: 4, interval: 3.0, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'phased', count: 5, interval: 2.0, pathId: 'main' },
        { monsterType: 'shielded', count: 18, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [
        { monsterType: 'swarm', count: 40, interval: 0.12, pathId: 'main' },
        { monsterType: 'flyer', count: 18, interval: 0.45, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [
        { monsterType: 'brute', count: 10, interval: 1.0, pathId: 'main' },
        { monsterType: 'juggernaut', count: 4, interval: 3.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 25, interval: 0.35, pathId: 'main' },
        { monsterType: 'phased', count: 5, interval: 2.0, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'shielded', count: 22, interval: 0.4, pathId: 'main' },
        { monsterType: 'swarm', count: 35, interval: 0.15, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'juggernaut', count: 5, interval: 2.5, pathId: 'main' },
        { monsterType: 'brute', count: 8, interval: 1.2, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'phased', count: 6, interval: 1.8, pathId: 'main' },
        { monsterType: 'flyer', count: 25, interval: 0.35, delay: 5, pathId: 'main' },
      ]},
    ],
    startingCoins: 700,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire', 'hive', 'trap', 'gem', 'lightning'],
    unlocks: ['V2'],
  },

  'V2': {
    id: 'V2',
    name: 'Ash Split',
    description: 'Multiple routes. Heavy air swarms.',
    difficulty: 5,
    region: 'volcano',
    regionNumber: 4,
    mapSize: { w: 26, h: 18 },
    base: { x: 13, y: 9 },
    playerSpawns: [{ x: 12, y: 9 }, { x: 14, y: 9 }],
    treeSlots: [
      { x: 3, y: 5 }, { x: 3, y: 13 },
      { x: 8, y: 7 }, { x: 8, y: 11 },
      { x: 18, y: 7 }, { x: 18, y: 11 },
      { x: 23, y: 5 }, { x: 23, y: 13 },
    ],
    paths: [{ id: 'main', routes: {
      'north': [{ x: -1, y: 3 }, { x: 5, y: 5 }, { x: 9, y: 7 }, { x: 13, y: 9 }],
      'south': [{ x: -1, y: 15 }, { x: 5, y: 13 }, { x: 9, y: 11 }, { x: 13, y: 9 }],
      'east': [{ x: 27, y: 9 }, { x: 21, y: 9 }, { x: 17, y: 9 }, { x: 13, y: 9 }],
    }, distribution: { 'north': 0.35, 'south': 0.35, 'east': 0.3 }, curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'flyer', count: 20, interval: 0.5, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'bat', count: 18, interval: 0.45, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'flyer', count: 15, interval: 0.5, pathId: 'main' },
        { monsterType: 'bat', count: 15, interval: 0.5, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'brute', count: 10, interval: 1.0, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'swarm', count: 40, interval: 0.12, pathId: 'main' },
        { monsterType: 'flyer', count: 20, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [{ monsterType: 'juggernaut', count: 5, interval: 2.5, pathId: 'main' }] },
      { waveNumber: 7, spawns: [
        { monsterType: 'phased', count: 6, interval: 1.8, pathId: 'main' },
        { monsterType: 'bat', count: 25, interval: 0.35, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 30, interval: 0.3, pathId: 'main' },
        { monsterType: 'shielded', count: 20, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'brute', count: 12, interval: 0.9, pathId: 'main' },
        { monsterType: 'juggernaut', count: 5, interval: 2.5, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'swarm', count: 50, interval: 0.1, pathId: 'main' },
        { monsterType: 'phased', count: 6, interval: 1.8, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'flyer', count: 35, interval: 0.25, pathId: 'main' },
        { monsterType: 'bat', count: 30, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 12, spawns: [
        { monsterType: 'juggernaut', count: 6, interval: 2.0, pathId: 'main' },
        { monsterType: 'shielded', count: 25, interval: 0.35, delay: 6, pathId: 'main' },
      ]},
    ],
    startingCoins: 725,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire', 'hive', 'trap', 'gem', 'lightning'],
    unlocks: ['V3', 'V2C'],
  },

  'V2C': {
    id: 'V2C',
    name: 'Volcano Challenge',
    description: 'Laser and AntiAir only. Pure air assault.',
    difficulty: 5,
    region: 'volcano',
    regionNumber: 4,
    isChallenge: true,
    mapSize: { w: 24, h: 14 },
    base: { x: 22, y: 7 },
    playerSpawns: [{ x: 12, y: 7 }, { x: 13, y: 7 }],
    treeSlots: [
      { x: 4, y: 5 }, { x: 4, y: 9 },
      { x: 10, y: 6 }, { x: 10, y: 8 },
      { x: 16, y: 5 }, { x: 16, y: 9 },
    ],
    paths: [{ id: 'main', points: [
      { x: -1, y: 7 }, { x: 5, y: 7 }, { x: 11, y: 7 }, { x: 17, y: 7 }, { x: 22, y: 7 },
    ], curved: false }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'bat', count: 15, interval: 0.6, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 15, interval: 0.6, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'bat', count: 12, interval: 0.5, pathId: 'main' },
        { monsterType: 'flyer', count: 12, interval: 0.5, delay: 4, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'flyer', count: 25, interval: 0.4, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'bat', count: 20, interval: 0.4, pathId: 'main' },
        { monsterType: 'flyer', count: 15, interval: 0.5, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [{ monsterType: 'flyer', count: 30, interval: 0.35, pathId: 'main' }] },
      { waveNumber: 7, spawns: [
        { monsterType: 'bat', count: 25, interval: 0.35, pathId: 'main' },
        { monsterType: 'flyer', count: 20, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [{ monsterType: 'flyer', count: 40, interval: 0.25, pathId: 'main' }] },
      { waveNumber: 9, spawns: [
        { monsterType: 'bat', count: 35, interval: 0.25, pathId: 'main' },
        { monsterType: 'flyer', count: 30, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [{ monsterType: 'flyer', count: 50, interval: 0.2, pathId: 'main' }] },
      { waveNumber: 11, spawns: [
        { monsterType: 'bat', count: 40, interval: 0.2, pathId: 'main' },
        { monsterType: 'flyer', count: 40, interval: 0.2, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 12, spawns: [{ monsterType: 'flyer', count: 60, interval: 0.15, pathId: 'main' }] },
    ],
    startingCoins: 400,
    startingGems: 10,
    startingVillagers: 15,
    allowedTowers: ['laser', 'antiair'],
    unlocks: [],
  },

  'V3': {
    id: 'V3',
    name: 'Magma Loops',
    description: 'Multi-route probability waves. Almost there.',
    difficulty: 5,
    region: 'volcano',
    regionNumber: 4,
    mapSize: { w: 28, h: 18 },
    base: { x: 14, y: 9 },
    playerSpawns: [{ x: 13, y: 9 }, { x: 15, y: 9 }],
    treeSlots: [
      { x: 3, y: 5 }, { x: 3, y: 13 },
      { x: 8, y: 7 }, { x: 8, y: 11 },
      { x: 20, y: 7 }, { x: 20, y: 11 },
      { x: 25, y: 5 }, { x: 25, y: 13 },
    ],
    paths: [{ id: 'main', routes: {
      'north': [{ x: -1, y: 3 }, { x: 5, y: 5 }, { x: 10, y: 7 }, { x: 14, y: 9 }],
      'south': [{ x: -1, y: 15 }, { x: 5, y: 13 }, { x: 10, y: 11 }, { x: 14, y: 9 }],
      'east': [{ x: 29, y: 9 }, { x: 23, y: 9 }, { x: 18, y: 9 }, { x: 14, y: 9 }],
      'ne': [{ x: 29, y: 3 }, { x: 23, y: 5 }, { x: 18, y: 7 }, { x: 14, y: 9 }],
    }, distribution: { 'north': 0.25, 'south': 0.25, 'east': 0.25, 'ne': 0.25 }, curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 22, interval: 0.5, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 25, interval: 0.4, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'brute', count: 12, interval: 0.9, pathId: 'main' },
        { monsterType: 'swarm', count: 35, interval: 0.15, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'juggernaut', count: 6, interval: 2.0, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'phased', count: 7, interval: 1.5, pathId: 'main' },
        { monsterType: 'shielded', count: 22, interval: 0.4, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [
        { monsterType: 'swarm', count: 50, interval: 0.1, pathId: 'main' },
        { monsterType: 'flyer', count: 25, interval: 0.35, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [
        { monsterType: 'brute', count: 14, interval: 0.8, pathId: 'main' },
        { monsterType: 'juggernaut', count: 6, interval: 2.0, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 35, interval: 0.25, pathId: 'main' },
        { monsterType: 'bat', count: 30, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'phased', count: 8, interval: 1.3, pathId: 'main' },
        { monsterType: 'shielded', count: 25, interval: 0.35, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'swarm', count: 60, interval: 0.08, pathId: 'main' },
        { monsterType: 'brute', count: 12, interval: 0.9, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'juggernaut', count: 7, interval: 1.8, pathId: 'main' },
        { monsterType: 'phased', count: 7, interval: 1.5, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 12, spawns: [
        { monsterType: 'flyer', count: 40, interval: 0.2, pathId: 'main' },
        { monsterType: 'shielded', count: 30, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 13, spawns: [
        { monsterType: 'swarm', count: 70, interval: 0.07, pathId: 'main' },
        { monsterType: 'juggernaut', count: 6, interval: 2.0, delay: 5, pathId: 'main' },
        { monsterType: 'phased', count: 6, interval: 1.8, delay: 8, pathId: 'main' },
      ]},
    ],
    startingCoins: 775,
    startingGems: 10,
    startingVillagers: 20,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire', 'hive', 'trap', 'gem', 'lightning'],
    unlocks: ['V4'],
  },

  'V4': {
    id: 'V4',
    name: 'Core Heart',
    description: 'The final battle. Defeat the Giant Bug.',
    difficulty: 5,
    region: 'volcano',
    regionNumber: 4,
    isGate: true,
    mapSize: { w: 28, h: 18 },
    base: { x: 14, y: 9 },
    playerSpawns: [{ x: 13, y: 9 }, { x: 15, y: 9 }],
    treeSlots: [
      { x: 3, y: 5 }, { x: 3, y: 13 },
      { x: 8, y: 7 }, { x: 8, y: 11 },
      { x: 20, y: 7 }, { x: 20, y: 11 },
      { x: 25, y: 5 }, { x: 25, y: 13 },
    ],
    paths: [{ id: 'main', routes: {
      'north': [{ x: -1, y: 3 }, { x: 5, y: 5 }, { x: 10, y: 7 }, { x: 14, y: 9 }],
      'south': [{ x: -1, y: 15 }, { x: 5, y: 13 }, { x: 10, y: 11 }, { x: 14, y: 9 }],
      'east': [{ x: 29, y: 9 }, { x: 23, y: 9 }, { x: 18, y: 9 }, { x: 14, y: 9 }],
      'ne': [{ x: 29, y: 3 }, { x: 23, y: 5 }, { x: 18, y: 7 }, { x: 14, y: 9 }],
    }, distribution: { 'north': 0.25, 'south': 0.25, 'east': 0.25, 'ne': 0.25 }, curved: true, curveResolution: 10 }],
    waves: [
      { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 25, interval: 0.45, pathId: 'main' }] },
      { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 28, interval: 0.35, pathId: 'main' }] },
      { waveNumber: 3, spawns: [
        { monsterType: 'brute', count: 14, interval: 0.8, pathId: 'main' },
        { monsterType: 'swarm', count: 40, interval: 0.12, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 4, spawns: [{ monsterType: 'juggernaut', count: 7, interval: 1.8, pathId: 'main' }] },
      { waveNumber: 5, spawns: [
        { monsterType: 'phased', count: 8, interval: 1.3, pathId: 'main' },
        { monsterType: 'shielded', count: 25, interval: 0.35, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 6, spawns: [
        { monsterType: 'swarm', count: 60, interval: 0.08, pathId: 'main' },
        { monsterType: 'flyer', count: 30, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 7, spawns: [
        { monsterType: 'brute', count: 16, interval: 0.7, pathId: 'main' },
        { monsterType: 'juggernaut', count: 7, interval: 1.8, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 8, spawns: [
        { monsterType: 'flyer', count: 40, interval: 0.2, pathId: 'main' },
        { monsterType: 'bat', count: 35, interval: 0.25, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 9, spawns: [
        { monsterType: 'phased', count: 10, interval: 1.0, pathId: 'main' },
        { monsterType: 'shielded', count: 30, interval: 0.3, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 10, spawns: [
        { monsterType: 'swarm', count: 70, interval: 0.07, pathId: 'main' },
        { monsterType: 'brute', count: 14, interval: 0.8, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 11, spawns: [
        { monsterType: 'juggernaut', count: 8, interval: 1.5, pathId: 'main' },
        { monsterType: 'phased', count: 8, interval: 1.3, delay: 6, pathId: 'main' },
      ]},
      { waveNumber: 12, spawns: [
        { monsterType: 'flyer', count: 45, interval: 0.18, pathId: 'main' },
        { monsterType: 'shielded', count: 35, interval: 0.25, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 13, spawns: [
        { monsterType: 'swarm', count: 80, interval: 0.06, pathId: 'main' },
        { monsterType: 'brute', count: 16, interval: 0.7, delay: 5, pathId: 'main' },
      ]},
      { waveNumber: 14, spawns: [
        { monsterType: 'juggernaut', count: 10, interval: 1.2, pathId: 'main' },
        { monsterType: 'phased', count: 10, interval: 1.0, delay: 6, pathId: 'main' },
        { monsterType: 'flyer', count: 40, interval: 0.2, delay: 8, pathId: 'main' },
      ]},
      { waveNumber: 15, spawns: [], boss: { type: 'giant_bug', pathId: 'main', delay: 0 }},
    ],
    startingCoins: 800,
    startingGems: 10,
    startingVillagers: 25,
    allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'laser', 'mortar', 'ice', 'fire', 'hive', 'trap', 'gem', 'lightning'],
    unlocks: [],
  },
};

// ============================================
// Tree Generation Utilities
// ============================================

/**
 * Sample points along a path at regular intervals
 */
function samplePathPoints(points: Array<{ x: number; y: number }>, sampleDistance: number = 20): Array<{ x: number; y: number }> {
  const samples: Array<{ x: number; y: number }> = [];
  
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    const numSamples = Math.ceil(segmentLength / sampleDistance);
    
    for (let j = 0; j <= numSamples; j++) {
      const t = j / numSamples;
      samples.push({
        x: p1.x + dx * t,
        y: p1.y + dy * t,
      });
    }
  }
  
  return samples;
}

/**
 * Get minimum distance from a point to any path point
 */
function getDistanceToPath(
  x: number, 
  y: number, 
  pathSamples: Array<{ x: number; y: number }>
): number {
  let minDist = Infinity;
  for (const sample of pathSamples) {
    const dist = Math.sqrt((x - sample.x) ** 2 + (y - sample.y) ** 2);
    if (dist < minDist) minDist = dist;
  }
  return minDist;
}

/**
 * Check if a position is valid for tree placement
 */
function isValidTreePosition(
  x: number,
  y: number,
  pathSamples: Array<{ x: number; y: number }>,
  basePos: { x: number; y: number },
  playerSpawns: Array<{ x: number; y: number }>,
  existingTrees: Array<{ x: number; y: number }>,
  mapWidth: number,
  mapHeight: number
): boolean {
  const { SAFE_DISTANCE, MIN_TREE_SPACING, MAP_EDGE_PADDING, BASE_EXCLUSION, SPAWN_EXCLUSION } = TREE_GENERATION;
  
  // Check map bounds
  if (x < MAP_EDGE_PADDING || x > mapWidth - MAP_EDGE_PADDING) return false;
  if (y < MAP_EDGE_PADDING || y > mapHeight - MAP_EDGE_PADDING) return false;
  
  // Check distance from path (must be safe from monsters)
  const pathDist = getDistanceToPath(x, y, pathSamples);
  if (pathDist < SAFE_DISTANCE) return false;
  
  // Check distance from base
  const baseDist = Math.sqrt((x - basePos.x) ** 2 + (y - basePos.y) ** 2);
  if (baseDist < BASE_EXCLUSION) return false;
  
  // Check distance from player spawns
  for (const spawn of playerSpawns) {
    const spawnDist = Math.sqrt((x - spawn.x) ** 2 + (y - spawn.y) ** 2);
    if (spawnDist < SPAWN_EXCLUSION) return false;
  }
  
  // Check distance from other trees
  for (const tree of existingTrees) {
    const treeDist = Math.sqrt((x - tree.x) ** 2 + (y - tree.y) ** 2);
    if (treeDist < MIN_TREE_SPACING) return false;
  }
  
  return true;
}

/**
 * Generate tree positions for a scenario
 * @param extraMultiplier - multiply target count by this (e.g. 2 for experiment mode = double trees)
 */
function generateTreePositions(
  scenario: ScenarioDef,
  tileSize: number,
  extraMultiplier: number = 1
): Array<{ x: number; y: number; id: string }> {
  const { SAFE_DISTANCE, ARROW_RANGE, STRATEGIC_PERCENT, TREE_MULTIPLIER } = TREE_GENERATION;
  
  const mapWidth = scenario.mapSize.w * tileSize;
  const mapHeight = scenario.mapSize.h * tileSize;
  
  // Convert positions to world coordinates
  const tileToWorld = (tile: { x: number; y: number }) => ({
    x: (tile.x + 0.5) * tileSize,
    y: (tile.y + 0.5) * tileSize,
  });
  
  const basePos = tileToWorld(scenario.base);
  const playerSpawns = scenario.playerSpawns.map(tileToWorld);
  
  // Collect all path points from all paths/routes
  const allPathSamples: Array<{ x: number; y: number }> = [];
  
  for (const path of scenario.paths) {
    if (path.routes) {
      // Multi-route path
      for (const routePoints of Object.values(path.routes)) {
        const worldPoints = routePoints.map(tileToWorld);
        allPathSamples.push(...samplePathPoints(worldPoints, 15));
      }
    } else if (path.points) {
      // Simple path
      const worldPoints = path.points.map(tileToWorld);
      allPathSamples.push(...samplePathPoints(worldPoints, 15));
    }
  }
  
  // Calculate target tree count (3x original, then optional extra e.g. 2x for experiment)
  const originalCount = scenario.treeSlots.length;
  const targetCount = Math.floor(originalCount * TREE_MULTIPLIER * extraMultiplier);
  const strategicCount = Math.floor(targetCount * STRATEGIC_PERCENT);
  const outerCount = targetCount - strategicCount;
  
  const generatedTrees: Array<{ x: number; y: number; id: string }> = [];
  
  // Phase 1: Generate strategic trees (within arrow range of path)
  let strategicAttempts = 0;
  const maxAttempts = 5000;
  
  while (generatedTrees.length < strategicCount && strategicAttempts < maxAttempts) {
    strategicAttempts++;
    
    // Pick a random point near the path
    const pathSample = allPathSamples[Math.floor(Math.random() * allPathSamples.length)];
    const angle = Math.random() * Math.PI * 2;
    const distance = SAFE_DISTANCE + Math.random() * (ARROW_RANGE - SAFE_DISTANCE);
    
    const x = pathSample.x + Math.cos(angle) * distance;
    const y = pathSample.y + Math.sin(angle) * distance;
    
    if (isValidTreePosition(x, y, allPathSamples, basePos, playerSpawns, generatedTrees, mapWidth, mapHeight)) {
      // Verify it's actually within arrow range
      const pathDist = getDistanceToPath(x, y, allPathSamples);
      if (pathDist <= ARROW_RANGE) {
        generatedTrees.push({ x, y, id: `tree-${generatedTrees.length}` });
      }
    }
  }
  
  // Phase 2: Generate outer trees (can be further from path)
  let outerAttempts = 0;
  const targetTotal = strategicCount + outerCount;
  
  while (generatedTrees.length < targetTotal && outerAttempts < maxAttempts) {
    outerAttempts++;
    
    // Random position anywhere on map
    const x = TREE_GENERATION.MAP_EDGE_PADDING + Math.random() * (mapWidth - 2 * TREE_GENERATION.MAP_EDGE_PADDING);
    const y = TREE_GENERATION.MAP_EDGE_PADDING + Math.random() * (mapHeight - 2 * TREE_GENERATION.MAP_EDGE_PADDING);
    
    if (isValidTreePosition(x, y, allPathSamples, basePos, playerSpawns, generatedTrees, mapWidth, mapHeight)) {
      generatedTrees.push({ x, y, id: `tree-${generatedTrees.length}` });
    }
  }
  
  // Log generation stats
  const inRangeCount = generatedTrees.filter(t => getDistanceToPath(t.x, t.y, allPathSamples) <= ARROW_RANGE).length;
  console.log(`[TreeGen] ${scenario.id}: Generated ${generatedTrees.length} trees (${inRangeCount} strategic, target was ${targetCount})`);
  
  return generatedTrees;
}

// ============================================
// LevelLoader Class
// ============================================

class LevelLoaderManager {
  private loadedLevels: Map<string, LoadedLevel> = new Map();

  /**
   * Get list of all available scenario IDs
   */
  getAvailableScenarios(): string[] {
    return Object.keys(SCENARIOS);
  }

  /**
   * Get scenario definition by ID (without loading)
   */
  getScenarioDef(scenarioId: string): ScenarioDef | null {
    // Check custom levels first
    if (CustomLevelManager.isCustomLevel(scenarioId)) {
      return CustomLevelManager.getLevel(scenarioId);
    }
    return SCENARIOS[scenarioId] || null;
  }

  /**
   * Get all custom level IDs
   */
  getCustomLevelIds(): string[] {
    return CustomLevelManager.getAllLevels().map(level => level.id);
  }

  /**
   * Get monsters available in a region
   */
  getRegionMonsters(region: RegionType): MonsterType[] {
    return REGION_MONSTERS[region] || [];
  }

  /**
   * Load a scenario and convert to world coordinates
   * Supports both built-in and custom levels
   * @param options.treeCountMultiplier - extra multiplier for generated tree count (e.g. 2 = double trees)
   */
  loadScenario(scenarioId: string, options?: { treeCountMultiplier?: number }): LoadedLevel | null {
    const cacheKey = options?.treeCountMultiplier ? `${scenarioId}:trees${options.treeCountMultiplier}` : scenarioId;
    if (this.loadedLevels.has(cacheKey)) {
      return this.loadedLevels.get(cacheKey)!;
    }

    // Check for pending level from map editor (test without save / imported level)
    let scenario: ScenarioDef | null = null;
    let isCustomLevel = false;
    let hasManualTrees = false;

    const pendingLevel = CustomLevelManager.getAndClearPendingLevelForTest(scenarioId);
    if (pendingLevel) {
      scenario = pendingLevel;
      isCustomLevel = true;
      hasManualTrees = pendingLevel.hasManualTrees;
      console.log(`[LevelLoader] Loading pending level from editor: ${pendingLevel.name}`);
    }

    if (!scenario && CustomLevelManager.isCustomLevel(scenarioId)) {
      const customLevel = CustomLevelManager.getLevel(scenarioId);
      if (customLevel) {
        scenario = customLevel;
        isCustomLevel = true;
        hasManualTrees = customLevel.hasManualTrees;
        console.log(`[LevelLoader] Loading custom level: ${customLevel.name}`);
      }
    }

    // Fall back to built-in scenarios
    if (!scenario) {
      scenario = SCENARIOS[scenarioId];
    }

    if (!scenario) {
      console.error(`[LevelLoader] Scenario not found: ${scenarioId}`);
      return null;
    }

    // Default 90px tiles for 1920x1080 (fits 20x12 map perfectly)
    const tileSize = scenario.tileSize || 90;

    // Build grid config
    const gridConfig: GridConfig = {
      tileWidth: tileSize,
      tileHeight: tileSize,
      mapWidthTiles: scenario.mapSize.w,
      mapHeightTiles: scenario.mapSize.h,
      originX: 0,
      originY: 0,
    };

    // Convert tile to world helper
    const tileToWorld = (tile: TilePosition) => ({
      x: (tile.x + 0.5) * tileSize,
      y: (tile.y + 0.5) * tileSize,
    });

    // Convert base position
    const baseWorldPos = tileToWorld(scenario.base);

    // Convert player spawn positions
    const playerSpawnWorldPos = scenario.playerSpawns.map(tileToWorld);

    // Tree positions: use manual trees if set, otherwise generate dynamically
    let treeSlotWorldPos: Array<{ x: number; y: number; id: string }>;
    
    if (hasManualTrees && scenario.treeSlots.length > 0) {
      // Use manually placed trees from custom level
      console.log(`[LevelLoader] Using ${scenario.treeSlots.length} manually placed trees`);
      treeSlotWorldPos = scenario.treeSlots.map((tree, index) => ({
        x: (tree.x + 0.5) * tileSize,
        y: (tree.y + 0.5) * tileSize,
        id: tree.id || `tree-${index + 1}`,
      }));
    } else {
      // Generate tree positions dynamically based on constraints:
      // - Safe distance from path (monsters can't hit players on trees)
      // - 60% within arrow tower range
      // - 3x the original count (optionally more, e.g. 2x for experiment mode)
      const treeMultiplier = options?.treeCountMultiplier ?? 1;
      treeSlotWorldPos = generateTreePositions(scenario, tileSize, treeMultiplier);
    }

    // Convert and process paths
    const pathsWorld = scenario.paths.map(path => {
      if (path.routes) {
        // Multi-route path
        const routesWorld: Record<string, Array<{ x: number; y: number }>> = {};

        for (const [routeId, points] of Object.entries(path.routes)) {
          routesWorld[routeId] = points.map(tileToWorld);
        }

        // Calculate length from first route for reference
        const firstRoutePoints = Object.values(routesWorld)[0];
        const { segmentLengths, totalLength } = this.calculatePathLengths(firstRoutePoints);

        return {
          id: path.id,
          routes: routesWorld,
          distribution: path.distribution,
          curved: path.curved || false,
          curveResolution: path.curveResolution || 10,
          totalLength,
          segmentLengths,
        };
      } else {
        // Simple path
        const points = (path.points || []).map(tileToWorld);
        const { segmentLengths, totalLength } = this.calculatePathLengths(points);

        return {
          id: path.id,
          points,
          curved: path.curved || false,
          curveResolution: path.curveResolution || 10,
          totalLength,
          segmentLengths,
        };
      }
    });

    const loaded: LoadedLevel = {
      scenario,
      gridConfig,
      baseWorldPos,
      playerSpawnWorldPos,
      treeSlotWorldPos,
      pathsWorld,
    };
    // Only cache when using fixed manual tree positions; generated trees are randomized each load/restart
    if (hasManualTrees && scenario.treeSlots.length > 0) {
      this.loadedLevels.set(cacheKey, loaded);
    }

    console.log(`[LevelLoader] Loaded scenario: ${scenarioId} (region: ${scenario.region})`);
    return loaded;
  }

  private calculatePathLengths(points: Array<{ x: number; y: number }>): {
    segmentLengths: number[];
    totalLength: number;
  } {
    const segmentLengths: number[] = [];
    let totalLength = 0;

    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;
      const len = Math.sqrt(dx * dx + dy * dy);
      segmentLengths.push(len);
      totalLength += len;
    }

    return { segmentLengths, totalLength };
  }

  /**
   * Clear cached levels
   */
  clearCache(): void {
    this.loadedLevels.clear();
  }

  /**
   * Get wave count for a scenario
   */
  getWaveCount(scenarioId: string): number {
    // Check custom levels first
    if (CustomLevelManager.isCustomLevel(scenarioId)) {
      const customLevel = CustomLevelManager.getLevel(scenarioId);
      return customLevel?.waves.length || 0;
    }
    const scenario = SCENARIOS[scenarioId];
    return scenario?.waves.length || 0;
  }

  /**
   * Get wave data for a specific wave
   */
  getWaveData(scenarioId: string, waveNumber: number): WaveDef | null {
    // Check custom levels first
    if (CustomLevelManager.isCustomLevel(scenarioId)) {
      const customLevel = CustomLevelManager.getLevel(scenarioId);
      if (!customLevel) return null;
      return customLevel.waves.find(w => w.waveNumber === waveNumber) || null;
    }
    const scenario = SCENARIOS[scenarioId];
    if (!scenario) return null;
    return scenario.waves.find(w => w.waveNumber === waveNumber) || null;
  }

  /**
   * Get scenarios by region
   */
  getScenariosByRegion(region: RegionType): ScenarioDef[] {
    return Object.values(SCENARIOS).filter(s => s.region === region);
  }
}

// Singleton instance
export const LevelLoader = new LevelLoaderManager();
